import { BlobProvider } from '@react-pdf/renderer';
import { format } from 'date-fns/format';
import { FileText } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import styled from 'styled-components';
import BettingSlipPDF from '../../components/BettingSlipPDF';
import GenericButton from '../../components/GenericButton';
import GenericDatePicker from '../../components/GenericDatePicker';
import GenericDropdown from '../../components/GenericDropDown';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { BetViewRequest } from '../../models/BetViewRequest';
import { GameConfig } from '../../models/GameConfig';
import { getAllActiveGameConfigs } from '../../redux/entities/gameEntity';
import { fetchGameReportAction } from '../../redux/entities/transactionEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const DownloadButton = styled(Button)`
  background-color: #4a90e2;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  
  &:hover {
    background-color: #357abd;
  }
`;

const GameReportPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [request, setRequest] = useState<BetViewRequest>({} as BetViewRequest);
  const [resultTimeOptions, setResultTimeOptions] = useState<any[]>([]);
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [selectedGameType, setSelectedGameType] = useState<string>('');
  const [selectedResultTime, setSelectedResultTime] = useState<string>('');
  const dispatch = useAppDispatch();
  const {showModalStrip} = useModalStrip();
  const gameConfigs = useAppSelector((state) => state.entities.gameReducer.activeGameConfigs);
  const gameReport = useAppSelector((state) => state.entities.transactionReducer.gameReport);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const [gameTypeOptions] = useState<any[]>([
    { label: 'Single', value: 'Single' },
    { label: 'Patti', value: 'Patti' },
    { label: 'Digit', value: 'Digit' },
    { label: 'Burfi', value: 'Burfi' },
  ]);

  useEffect(() => {
    setSelectedDate(new Date());
    setRequest({} as BetViewRequest);
    setResultTimeOptions([]);
    setSelectedResultTime('');
    setSelectedGame('');
    setSelectedGameType('');
  }, [])

  useEffect(() => {
    const fetchGameConfigs = async () => {
      if ((!gameConfigs || gameConfigs.length === 0) && !isLoading && !hasFetched) {
        try {
          setIsLoading(true);
          await dispatch(getAllActiveGameConfigs()).unwrap();
          setHasFetched(true);
        } catch (err) {
          showModalStrip('danger', 'Failed to load game configurations', 5000);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchGameConfigs();
  }, [dispatch, gameConfigs, isLoading, hasFetched, showModalStrip]);

  // Group gameConfigs by game name
  const groupedGameConfigs = gameConfigs.reduce((acc, config) => {
    const gameName = config?.game?.name;
    if (!acc[gameName]) {
      acc[gameName] = [];
    }
    acc[gameName].push(config);
    return acc;
  }, {} as Record<string, GameConfig[]>);

  // Get the resultTimes based on selected game
  const getResultTimes = useCallback((gameName: string) => {
    const selectedGameConfig = groupedGameConfigs[gameName] || [];
    return selectedGameConfig.map((config: any) => config.resultTime);
  }, [groupedGameConfigs]);

  const getGameTypeOptions = () => {
    let game = groupedGameConfigs[selectedGame]
      .map((config: any) => config.game)[0]; // Access the first game object

    if (game?.digitSupported) { // Add optional chaining to handle undefined cases
      return gameTypeOptions;
    } else if (game?.burfiSupported) {
      return gameTypeOptions;
    } else {
      return gameTypeOptions.filter(x => x.value !== 'Digit' && x.value !== 'Burfi');
    }
  }


    useEffect(() => {
      if (selectedGame) {
        const resultTimes = getResultTimes(selectedGame).map(resultTime => ({
          value: resultTime.id.toString(),
          label: resultTime.resultTime,
        }));
    
        // Sorting the times based on the label (which is in 12-hour format like "10:30 AM")
        const sortedResultTimes = resultTimes.sort((a, b) => {
          const timeA = new Date(`01/01/2020 ${a.label}`); // Adding a fixed date to parse the time correctly
          const timeB = new Date(`01/01/2020 ${b.label}`);
          return timeA.getTime() - timeB.getTime();
        });
    
        setResultTimeOptions(sortedResultTimes);
      }
    }, [selectedGame, getResultTimes]);

  // Handle form submission (save bet)
  const fetchGameReport = () => {
    // Validation
    if (!selectedGame || !selectedResultTime || !selectedGameType) {
      alert('All fields are required');
      return;
    }

    dispatch(fetchGameReportAction(request))
      .unwrap()
      .then((response: any) => {
       // setResult(response);
      })
      .catch((err: any) => {
        showModalStrip('danger', err.message, 5000);
      });

  };

  // Calculate total points sum
  const totalPointsSum = gameReport?.games?.reduce((sum, game) => sum + (game.points || 0), 0) || 0;
  const totalBetsCount = gameReport?.totalBets || 0;

    const SafeDownloadButton: React.FC<{
    onClick: () => void;
    children: React.ReactNode;
  }> = DownloadButton as any;

  const PDFDownloadButton = () => (
    <BlobProvider document={
      <BettingSlipPDF 
        {...gameReport} 
        gameType={selectedGameType} 
        game={selectedGame}
        gameTime={resultTimeOptions.filter(x => x.value === selectedResultTime)[0]?.label}
        totalBets={totalBetsCount}
        totalPointsSum={totalPointsSum}
      />
    }>
      {({ url }) => (
        <SafeDownloadButton
            onClick={() => {
              if (url) {
                const link = document.createElement('a');
                link.href = url;
                link.download = `business-report-${format(selectedDate || new Date(), 'dd-MM-yyyy')}.pdf`;
                link.click();
              }
            }}
            >
          <FileText /> Download Report
        </SafeDownloadButton>
      )}
    </BlobProvider>
  );
  


  return (
    <Container>
      <PageHeading>Game Report</PageHeading>
      <TableWrapper>

        <GenericDatePicker 
          label='Select Date'
          value={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            setRequest({ ...request, date: format(date == null ? new Date() : date, "yyyy-MM-dd") });
          }
          }
        />
        
        <GenericDropdown
          label="Select Game"
          options={Object.keys(groupedGameConfigs).map((gameName) => ({
            value: gameName,
            label: gameName,
          }))}
          onChange={(gameName) => {
            setSelectedGame(gameName);
            setRequest({ ...request, gameId: groupedGameConfigs[gameName] && gameName?.id !== '0' ? groupedGameConfigs[gameName][0]?.game?.id : undefined });
          }}
          value={selectedGame}
        />

          {/* Select Result Time */}
          <GenericDropdown
          label="Select Result Time"
          options={resultTimeOptions}
          onChange={(resultTime) => {
            setSelectedResultTime(resultTime);
            setRequest({ ...request, resultTimeId: resultTime !== '' && resultTime !== '0' ? resultTime : undefined });
          }}
          value={selectedResultTime}
        />

        {selectedResultTime && (
          <GenericDropdown
            label="Select Game Type"
            options={getGameTypeOptions()}
            onChange={(gameType) => {
                setSelectedGameType(gameType);
                setRequest({ ...request, gameType: gameType });
            }}
            value={selectedGameType}
          />
        )}
        <GenericButton
          label="Submit"
          onClick={() => fetchGameReport()}
        />

      { gameReport && gameReport.games && gameReport.games.length > 0 &&
          <PDFDownloadButton />
        }

      </TableWrapper>
    </Container>
  );
};

export default GameReportPage;
